import React, { FC, useContext } from "react";
import { View, Text, StyleSheet } from "react-native";
import Spinner from "../../components/common/Spinner";
import StyledIcon from "../../components/StyledIcon";
import Colors from "../../constants/Colors";
import FontStyles from "../../constants/FontStyle";
import { AuthContext } from "../../contexts/auth/Store";
import useFetch from "../../hooks/useFetch";
import { IBuyer } from "../../interfaces/buyer";
import {
  ILedger,
  ILedgerLog,
  LedgerHead,
  LedgerSubhead,
} from "../../interfaces/ledger";
import { ledgerService } from "../../services/LedgerService";
import { timestampToDate } from "../../utils/date";
import { formatCurrency } from "../../utils/money";

const LogIcon: FC<{
  amount: number;
}> = ({ amount }) => {
  const iconProps =
    amount < 0
      ? {
          iconName: "arrow-up-right",
          fillColor: Colors.dangerBackground,
          iconColor: Colors.dangerText,
        }
      : {
          iconName: "arrow-down-left",
          fillColor: Colors.successBackground,
          iconColor: Colors.successText,
        };
  return (
    <View>
      <StyledIcon
        {...iconProps}
        iconLibrary="Feather"
        size={32}
        outlineColor="transparent"
      />
    </View>
  );
};

export const BalanceLogCard = () => {
  const auth = useContext(AuthContext);
  const buyer: IBuyer = auth.state.buyerData;

  const [ledger, loadingLedger]: [ILedger, boolean, any, any] = useFetch(
    ledgerService,
    "fetchByOwnerSpecific",
    [
      {
        head: LedgerHead.Buyer,
        subhead: LedgerSubhead.Wallet,
        owner: buyer?._id,
      },
    ],
    !!buyer?._id
  );

  const [ledgerLogs, isLoadingLedgerLogs, ,] = useFetch<ILedgerLog[]>(
    ledgerService,
    "fetchLogs",
    [ledger?._id],
    !!ledger?._id
  );

  return (
    <>
      <View style={styles.container}>
        <Text style={styles.header}>Transactions</Text>

        {/* this will be turned to a flatlist */}
        {loadingLedger || isLoadingLedgerLogs ? (
          <Spinner color={Colors.tintColor} />
        ) : !ledgerLogs?.length ? (
          <Text style={styles.logSubTitle}>
            You currently do not have any transactions.
          </Text>
        ) : (
          <View style={styles.listContainer}>
            {/* a flatlist item */}
            {ledgerLogs.map((ledgerLog) => (
              <View key={ledgerLog._id} style={styles.logContainer}>
                <LogIcon amount={ledgerLog.value < 0 ? -1 : 1} />
                <View style={styles.logDetailContainer}>
                  <Text style={styles.logTitle}>{ledgerLog.tag}</Text>
                  <Text style={styles.logSubTitle}>
                    {ledgerLog?.description?.split(`|`).join(` | `)}
                  </Text>
                </View>

                <View style={styles.logAmountContainer}>
                  <Text
                    style={[
                      styles.logSubTitle,
                      {
                        color:
                          ledgerLog.value < 0
                            ? Colors.dangerText
                            : Colors.successText,
                      },
                    ]}
                  >
                    {formatCurrency(ledgerLog.value)}
                  </Text>
                  <Text style={styles.logSubTitle}>
                    {timestampToDate(ledgerLog.created)}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {},
  header: {
    ...FontStyles.size14,
    ...FontStyles.lh16,
    ...FontStyles.medium,
    ...FontStyles.gray,
    marginVertical: 16,
  },
  listContainer: {},
  logContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    alignContent: "center",
    justifyContent: "center",
    marginBottom: 24,
  },
  logDetailContainer: {
    flex: 1,
    paddingHorizontal: 6,
  },
  logAmountContainer: {
    flex: 1,
    alignItems: "flex-end",
  },
  logSubTitle: {
    ...FontStyles.size14,
    ...FontStyles.lh16,
    ...FontStyles.gray,
    ...FontStyles.normal,
    paddingBottom: 4,
  },
  logTitle: {
    ...FontStyles.size14,
    ...FontStyles.lh16,
    ...FontStyles.medium,
    paddingBottom: 4,
    textTransform: "capitalize",
  },
});
